import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { IcSearch, IcTruck, IcClose, IcPlus, IcMinus, IcPackage, IcFlame, IcPhone, IcClipboardList, IcAlertTriangle, IcCheckCircle, IcTrophy, IcFlask, IcFilters } from '../../theme/icons';
import Swal from '../../theme/swal';
import commandeService from '../../services/commandeService';
import supplierService from '../../services/supplierService';
import productService from '../../services/productService';
import CommandeTabs from '../../components/CommandeTabs';
import { normProductName, duplicateProductAlert } from '../../utils/duplicateProduct';
import KI from '../../theme/palette';
import { BtnSk } from '../../components/Skeleton';
import { filterSearch } from '../../utils/search';

const PAGE_SIZE = 60; // rendu progressif : +60 cartes à chaque approche du bas

// Même design que « Nouvelle vente » (SellerSalesPage) : recherche + filtres,
// carte catalogue à gauche, panneau collant « Bon de commande » à droite
// (recherche fournisseur + lignes + total), popup « Détails ». Mais côté
// achat : prix fournisseurs, et à la fin on crée un bon de commande.
const ACommander = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Mode « Continuer la commande » : on arrive depuis un BC brouillon avec ses
  // lignes — le panier est prérempli et la validation MET À JOUR ce bon.
  const continueOrder = location.state?.continueOrder || null;
  const [continueLoaded, setContinueLoaded] = useState(false);

  const [lowStock, setLowStock] = useState([]);        // produits ≤ stock_min (+ prix par fournisseur, meilleures ventes d'abord)
  const [products, setProducts] = useState([]);         // tous les produits (recherche / images)
  const [soldMap, setSoldMap] = useState({});           // ventes 30j de TOUS les produits {id: qty}
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('produit'); // produit | labo
  const [searchOpen, setSearchOpen] = useState(false);     // liste de résultats sous la recherche (comme AddFacturation)
  const [newProdPop, setNewProdPop] = useState(null);      // {name, cost, qty} — popup « Nouveau produit » hors catalogue
  const newProdSeqRef = useRef(0);
  const newProdNameRef = useRef(null);                     // input nom du popup (focus sur « Modifier le nom »)
  const [stockFilter, setStockFilter] = useState('faible');   // faible (défaut) | rupture | tous
  const [showFilters, setShowFilters] = useState(false);  // panneau « Filtres » (stock) sous la barre de recherche
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [addPop, setAddPop] = useState(null);           // {item, qty, price} — popup Ajouter (drafts string)
  const [pricePop, setPricePop] = useState(null);       // {item, loading} — popup prix par fournisseur (TTC)
  const [fichePop, setFichePop] = useState(null);       // {item, achat, vente, marge} — 2e carte : maj fiche produit
  const [showPanier, setShowPanier] = useState(false);
  const [panierSupplierId, setPanierSupplierId] = useState('');
  const [panierSupQuery, setPanierSupQuery] = useState('');   // recherche fournisseur du panier
  const [panierSupOpen, setPanierSupOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [acmd, suppliersData, productsData] = await Promise.all([
          commandeService.getACommander(),
          supplierService.getAllSuppliers(),
          productService.getAllProducts(),
        ]);
        // Réservations de produits pas encore dans la liste : cartes
        // « Nouveau produit » en tête du catalogue.
        const newRows = (acmd.new_reservations || []).map((r) => ({
          id: `new-${r.reservation_id}`,
          is_new: true,
          name: r.name,
          code: null,
          stock: 0,
          stock_min: 0,
          current_cost: r.estimated_cost || 0,
          current_cost_ttc: r.estimated_cost || 0,
          reserved_qty: r.quantity || 1,
          suggested_qty: r.quantity || 1,
          sold_qty: 0,
          suppliers: [],
          pending_order: null,
          customer_name: r.customer_name,
        }));
        setLowStock([...newRows, ...(acmd.products || [])]);
        setSoldMap(acmd.sold_30j || {});
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
        setProducts(productsData.results || productsData || []);
      } catch (err) {
        console.error('Erreur chargement:', err);
        Swal.fire('Erreur', 'Impossible de charger les données', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Préremplissage du panier depuis le BC brouillon (une fois les produits
  // chargés, pour récupérer images/stock), puis historique prix par ligne.
  useEffect(() => {
    if (!continueOrder || continueLoaded || loading) return;
    setContinueLoaded(true);
    setPanierSupplierId(String(continueOrder.supplier || ''));
    setNotes(continueOrder.notes || '');
    const byId = {};
    products.forEach((p) => { byId[p.id] = p; });
    const lines = (continueOrder.items || []).map((it, idx) => {
      const p = byId[it.product] || {};
      return {
        id: it.product ?? `new-item-${idx}`,
        is_new: !it.product,
        name: it.product_name || p.name || '',
        code: p.code,
        image: p.image || null,
        stock: p.stock ?? it.product_stock ?? 0,
        stock_min: p.stock_min,
        sold_qty: null,
        cost: p.cost || 0,
        suppliers: null,
        pending_order: null,
        suggested_qty: parseInt(it.quantity) || 1,
        quantity: parseInt(it.quantity) || 1,
        free_qty: parseInt(it.free_quantity) || 0,
        custom_cost: parseFloat(it.expected_cost) || 0,
        check_status: it.check_status || '',
      };
    });
    setCart(lines);
    lines.forEach((l) => {
      if (l.is_new) return; // pas de fiche ni d'historique pour un nouveau produit
      commandeService.getComparison(l.id)
        .then((data) => {
          setCart((prev) => prev.map((x) => (x.id === l.id
            ? {
                ...x,
                suppliers: data.suppliers || [],
                pending_order: data.pending_order,
                current_cost_ttc: data.current_cost_ttc,
                reserved_qty: data.reserved_qty,
              }
            : x)));
        })
        .catch((e) => console.error('Erreur comparaison prix:', e));
    });
  }, [continueOrder, continueLoaded, loading, products]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----- Garde de sortie : panier non vide + navigation vers une autre page
  // → proposer d'enregistrer en brouillon ou d'annuler la commande. -----
  useEffect(() => {
    if (cart.length === 0) return undefined;

    const onLeaveClick = (e) => {
      const a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      // Liens externes / téléphone / ancres / même page : laisser passer.
      if (!href.startsWith('/') || href.startsWith('/commandes/a-commander')) return;
      e.preventDefault();
      e.stopPropagation();
      Swal.fire({
        title: 'Commande en cours',
        text: `${cart.length} produit(s) dans la sélection — que faire avant de quitter ?`,
        icon: 'question',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Enregistrer en brouillon',
        denyButtonText: 'Annuler la commande',
        cancelButtonText: 'Rester ici',
        confirmButtonColor: KI.ramps.blue[600],
        denyButtonColor: KI.coralDark,
      }).then(async (r) => {
        if (r.isConfirmed) {
          try {
            const orderId = await saveDraft();
            if (!orderId) {
              Swal.fire('Fournisseur requis',
                'Aucun fournisseur identifiable pour ce brouillon — choisissez-le dans le bon de commande.',
                'info');
              openPanier();
              return;
            }
            setCart([]);
            setNotes('');
            navigate(href);
          } catch (err) {
            console.error('Erreur sauvegarde brouillon:', err);
            Swal.fire('Erreur', 'Échec de l\'enregistrement du brouillon', 'error');
          }
        } else if (r.isDenied) {
          setCart([]);
          setNotes('');
          navigate(href);
        }
        // Rester ici : ne rien faire.
      });
    };

    // Fermeture / rechargement de l'onglet : confirmation native.
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    document.addEventListener('click', onLeaveClick, true);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('click', onLeaveClick, true);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [cart, notes, panierSupplierId, continueOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const productById = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.id] = p; });
    return m;
  }, [products]);

  const lowById = useMemo(() => {
    const m = {};
    lowStock.forEach((p) => { m[p.id] = p; });
    return m;
  }, [lowStock]);

  // Nb de produits à commander fournissables par chaque fournisseur.
  const supplierCounts = useMemo(() => {
    const counts = {};
    lowStock.forEach((p) => (p.suppliers || []).forEach((s) => {
      counts[s.supplier_id] = (counts[s.supplier_id] || 0) + 1;
    }));
    return counts;
  }, [lowStock]);

  const sortedSuppliers = useMemo(() => (
    [...suppliers].sort((a, b) => (supplierCounts[b.id] || 0) - (supplierCounts[a.id] || 0))
  ), [suppliers, supplierCounts]);

  const supplierById = (id) => suppliers.find((s) => s.id === parseInt(id)) || null;

  // Suggestions du champ fournisseur du panier (recherche par nom, les plus
  // utiles d'abord).
  const panierSupMatches = useMemo(() => {
    // Règle Ki Para (utils/search.js) : début de mot, « commence par » en tête.
    return filterSearch(sortedSuppliers, panierSupQuery, { text: (s) => s.name }).slice(0, 8);
  }, [panierSupQuery, sortedSuppliers]);

  const histFor = (supplierList, sid) =>
    (supplierList || []).find((s) => s.supplier_id === parseInt(sid)) || null;

  // Tous les prix affichés sur cette page sont des prix d'achat TTC (coût de
  // la ligne de facture majoré de sa TVA — même règle que la fiche produit).
  const ttcOf = (hist) => (hist ? (hist.last_cost_ttc ?? hist.last_cost) : 0);

  // Fournisseur « actif » pour l'affichage des prix : celui du panier.
  const activeSupplierId = panierSupplierId || '';

  // Prix estimé d'un produit : dernier prix chez le fournisseur actif, sinon
  // meilleur prix connu, sinon coût actuel.
  const priceInfo = (item) => {
    if (item.custom_cost != null) return { price: item.custom_cost, from: null, exact: true, custom: true };
    const hist = activeSupplierId ? histFor(item.suppliers, activeSupplierId) : null;
    if (hist) return { price: ttcOf(hist), from: null, exact: true };
    const best = (item.suppliers || [])[0];
    if (best) return { price: ttcOf(best), from: best.supplier_name, exact: false };
    return { price: item.cost || 0, from: null, exact: false };
  };

  // ----- Catalogue -----
  // Recherche vide → liste « à commander » (meilleures ventes d'abord).
  // Recherche → tous les produits (pour pouvoir tout ajouter).
  const catalog = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    // Les « nouveaux produits réservés » (hors liste produits) vivent dans
    // lowStock — ils doivent aussi apparaître en recherche et en mode Tous.
    const newRows = lowStock.filter((r) => r.is_new).map((r) => ({ ...r, image: null, cost: r.current_cost_ttc }));
    let items;
    if (q) {
      // Règle Ki Para (utils/search.js) : début de mot sur le nom (ou le
      // laboratoire), code « contient », « commence par » en tête.
      items = searchMode === 'labo' ? [] : filterSearch(newRows, searchQuery, { text: (r) => r.name });
      items = items.concat(filterSearch(products, searchQuery, searchMode === 'labo'
          ? { text: (p) => p.laboratoire_name }
          : { text: (p) => p.name, codes: (p) => [p.code] })
        .map((p) => {
          const low = lowById[p.id];
          return low
            ? { ...low, image: p.image, cost: low.current_cost_ttc ?? low.current_cost }
            : {
                id: p.id, name: p.name, code: p.code, image: p.image,
                stock: p.stock, stock_min: p.stock_min, sold_qty: soldMap[p.id] || 0,
                cost: p.cost || 0, suppliers: null, pending_order: null,
                suggested_qty: 1,
              };
        }));
    } else if (stockFilter === 'tous') {
      items = products.map((p) => {
        const low = lowById[p.id];
        return low
          ? { ...low, image: p.image, cost: low.current_cost_ttc ?? low.current_cost }
          : {
              id: p.id, name: p.name, code: p.code, image: p.image,
              stock: p.stock, stock_min: p.stock_min, sold_qty: soldMap[p.id] || 0,
              cost: p.cost || 0, suppliers: null, pending_order: null,
              suggested_qty: 1,
            };
      });
      items = newRows.concat(items);
      // Ordre demandé : réservés → à commander vendus 30j (décroissant, se
      // termine sur les « 1 vendu ») → ruptures de stock → à commander
      // jamais vendus (30j) → enfin les produits normaux en stock, du plus
      // vendu au moins vendu.
      const bucket = (p) => {
        if (p.reserved_qty > 0) return 0;
        if (p.stock <= 0) return 2;
        if (lowById[p.id]) return (p.sold_qty || 0) > 0 ? 1 : 3;
        return 4;
      };
      items.sort((a, b) => (
        bucket(a) - bucket(b)
        || (b.sold_qty || 0) - (a.sold_qty || 0)
        || a.name.localeCompare(b.name)
      ));
    } else {
      items = lowStock.map((p) => ({
        ...p,
        image: productById[p.id] ? productById[p.id].image : null,
        cost: p.current_cost_ttc ?? p.current_cost,
      }));
      if (stockFilter === 'rupture') items = items.filter((p) => p.stock <= 0);
    }
    return items;
  }, [searchQuery, searchMode, stockFilter, lowStock, products, soldMap, lowById, productById]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retour en haut de pagination quand la liste change (recherche / filtres).
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, searchMode, stockFilter]);

  // ----- Panier -----
  const addToCart = (item, qty = null, customCost = undefined, freeQty = undefined) => {
    setCart((prev) => {
      const existing = prev.find((l) => l.id === item.id);
      if (existing) {
        return prev.map((l) => (l.id === item.id
          ? {
              ...l,
              quantity: qty ?? l.quantity + 1,
              ...(customCost !== undefined ? { custom_cost: customCost } : {}),
              ...(freeQty !== undefined ? { free_qty: freeQty } : {}),
            }
          : l));
      }
      return [...prev, {
        ...item,
        quantity: qty ?? (item.suggested_qty || 1),
        ...(customCost !== undefined ? { custom_cost: customCost } : {}),
        ...(freeQty !== undefined ? { free_qty: freeQty } : {}),
      }];
    });
    // Produit sans historique de prix chargé (venu de la recherche) → charger.
    if (!item.suppliers) {
      commandeService.getComparison(item.id)
        .then((data) => {
          setCart((prev) => prev.map((l) => (
            l.id === item.id
              ? {
                  ...l,
                  suppliers: data.suppliers || [],
                  pending_order: data.pending_order,
                  current_cost_ttc: data.current_cost_ttc,
                  reserved_qty: data.reserved_qty,
                  cost: data.current_cost_ttc ?? l.cost,
                }
              : l
          )));
        })
        .catch((e) => console.error('Erreur comparaison prix:', e));
    }
  };

  // ----- « Nouveau produit » hors catalogue (même esprit que le popup
  // « Passer une commande » de la vente) : le produit n'est PAS créé dans le
  // catalogue — seule sa désignation part sur le bon (product_name côté API).
  const openNewProdPop = () => {
    setSearchOpen(false);
    setNewProdPop({ name: searchQuery.trim(), cost: '', qty: '1' });
  };

  // Détection de doublon pendant la saisie du nom : correspondance exacte
  // (même nom normalisé) ou produits proches du catalogue.
  const newProdMatches = useMemo(() => {
    if (!newProdPop) return { exact: null, similar: [] };
    const q = normProductName(newProdPop.name);
    if (q.length < 2) return { exact: null, similar: [] };
    let exact = null;
    const similar = [];
    for (const p of products) {
      const n = normProductName(p.name);
      if (n === q) { if (!exact) exact = p; continue; }
      if (q.length >= 3 && similar.length < 3 && (n.includes(q) || q.includes(n))) similar.push(p);
    }
    return { exact, similar };
  }, [newProdPop, products]);

  // « Utiliser ce produit » : la ligne part avec la vraie fiche catalogue
  // (id produit, historique prix) au lieu d'une désignation hors catalogue.
  const pickExistingProduct = (p) => {
    if (!newProdPop) return;
    const qty = Math.max(1, parseInt(newProdPop.qty, 10) || 1);
    const parsed = parseFloat(String(newProdPop.cost).replace(',', '.'));
    const customCost = Number.isFinite(parsed) && parsed > 0 ? +parsed.toFixed(3) : undefined;
    const low = lowById[p.id];
    const item = low
      ? { ...low, image: p.image, cost: low.current_cost_ttc ?? low.current_cost }
      : {
          id: p.id, name: p.name, code: p.code, image: p.image,
          stock: p.stock, stock_min: p.stock_min, sold_qty: soldMap[p.id] || 0,
          cost: p.cost || 0, suppliers: null, pending_order: null,
          suggested_qty: 1,
        };
    addToCart(item, qty, customCost);
    setNewProdPop(null);
    setSearchQuery('');
  };

  const confirmNewProdPop = () => {
    if (!newProdPop) return;
    const name = newProdPop.name.trim();
    if (!name) { Swal.fire('Nom requis', 'Entrez le nom du nouveau produit.', 'info'); return; }
    // Même nom qu'un produit du catalogue → ne pas créer un doublon en
    // silence : proposer d'utiliser la fiche existante ou de renommer.
    const dup = newProdMatches.exact;
    if (dup) {
      duplicateProductAlert({ product: dup, name, confirmText: 'Utiliser le produit existant' }).then((r) => {
        if (r.isConfirmed) {
          pickExistingProduct(dup);
        } else {
          setTimeout(() => { newProdNameRef.current?.focus(); newProdNameRef.current?.select(); }, 50);
        }
      });
      return;
    }
    const parsed = parseFloat(String(newProdPop.cost).replace(',', '.'));
    const price = Number.isFinite(parsed) && parsed >= 0 ? +parsed.toFixed(3) : 0;
    const qty = Math.max(1, parseInt(newProdPop.qty, 10) || 1);
    newProdSeqRef.current += 1;
    // suppliers: [] (et non null) → addToCart ne tente pas de charger un
    // historique de prix pour un id qui n'existe pas encore.
    addToCart({
      id: `newp-${newProdSeqRef.current}`,
      is_new: true,
      name,
      code: null,
      image: null,
      stock: 0,
      stock_min: 0,
      sold_qty: 0,
      suggested_qty: qty,
      cost: price,
      current_cost: price,
      current_cost_ttc: price,
      suppliers: [],
      pending_order: null,
    }, qty, price);
    setNewProdPop(null);
    setSearchQuery('');
  };

  // Résultats affichés dans la liste déroulante sous la recherche : le même
  // catalogue filtré que la grille, limité aux premiers résultats.
  const searchMatches = useMemo(
    () => (searchQuery.trim() ? catalog.slice(0, 8) : []),
    [catalog, searchQuery],
  );

  // Popup « Prix fournisseurs » : historique TTC par fournisseur, meilleur
  // prix en premier. Charge la comparaison si le produit vient de la recherche.
  const openPricePop = (item) => {
    if (item.suppliers) {
      setPricePop({ item, loading: false });
      return;
    }
    setPricePop({ item, loading: true });
    commandeService.getComparison(item.id)
      .then((data) => {
        setPricePop((p) => (p && p.item.id === item.id
          ? {
              item: { ...item, ...data, image: item.image, cost: data.current_cost_ttc ?? item.cost },
              loading: false,
            }
          : p));
      })
      .catch((e) => {
        console.error('Erreur comparaison prix:', e);
        setPricePop((p) => (p && p.item.id === item.id ? { ...p, loading: false } : p));
      });
  };

  // Depuis le popup prix : choisir un fournisseur → préremplit l'ajout à ce prix.
  const chooseSupplierPrice = (hist) => {
    if (!pricePop) return;
    const { item } = pricePop;
    const line = cart.find((l) => l.id === item.id);
    setAddPop({
      item,
      qty: String(line ? 1 : (item.suggested_qty || 1)), // ligne existante → additif (« ajouter encore », owner Studio 2026-08-13)
      price: Number(ttcOf(hist) || 0).toFixed(3),
    });
    setPricePop(null);
  };

  // Popup « Ajouter » : prix + quantité (+ éventuelles unités gratuites
  // offertes par le fournisseur) avant d'ajouter au bon de commande.
  const openAddPop = (item) => {
    const line = cart.find((l) => l.id === item.id);
    const info = priceInfo(line || item);
    setAddPop({
      item,
      qty: String(line ? 1 : (item.suggested_qty || 1)), // ligne existante → additif (« ajouter encore », owner Studio 2026-08-13)
      price: Number(info.price || 0).toFixed(3),
      initialPrice: Number(info.price || 0).toFixed(3),
      showFree: !!(line && line.free_qty > 0),
      freeQty: String(line && line.free_qty > 0 ? line.free_qty : 1),
    });
  };

  // Liaison achat ⇄ vente ⇄ marge de la 2e carte fiche (drafts string).
  const parseNum = (v) => parseFloat(String(v).replace(',', '.'));
  const setFicheAchat = (value) => setFichePop((prev) => {
    const achat = parseNum(value);
    const vente = parseNum(prev.vente);
    // Le prix d'achat bouge → la vente est conservée, la marge se recalcule.
    const marge = (Number.isFinite(achat) && achat > 0 && Number.isFinite(vente))
      ? (((vente - achat) / achat) * 100).toFixed(1)
      : prev.marge;
    return { ...prev, achat: value, marge };
  });
  const setFicheVente = (value) => setFichePop((prev) => {
    const achat = parseNum(prev.achat);
    const vente = parseNum(value);
    const marge = (Number.isFinite(achat) && achat > 0 && Number.isFinite(vente))
      ? (((vente - achat) / achat) * 100).toFixed(1)
      : prev.marge;
    return { ...prev, vente: value, marge };
  });
  const setFicheMarge = (value) => setFichePop((prev) => {
    const achat = parseNum(prev.achat);
    const marge = parseNum(value);
    const vente = (Number.isFinite(achat) && achat > 0 && Number.isFinite(marge))
      ? (achat * (1 + marge / 100)).toFixed(3)
      : prev.vente;
    return { ...prev, marge: value, vente };
  });

  const saveFiche = async () => {
    if (!fichePop || fichePop.saving) return;
    const achat = parseNum(fichePop.achat);
    if (!Number.isFinite(achat) || achat < 0) return;
    setFichePop((f) => ({ ...f, saving: true }));
    await patchFiche(fichePop.item, achat, fichePop.vente);
    setFichePop(null);
  };

  // Prix d'achat modifié dans le popup Ajouter + case « Mettre à jour la
  // fiche » cochée → PATCH direct de la fiche (achat + vente), sans popup
  // supplémentaire. La fiche stocke le prix d'achat SAISI (HT quand le
  // produit est facturé) ; on convertit le TTC entré avec le même ratio que
  // product_prix_achat_ttc.
  const patchFiche = async (item, achatTTC, venteStr) => {
    const ratio = (item.current_cost_ttc > 0 && item.current_cost > 0)
      ? item.current_cost_ttc / item.current_cost
      : 1;
    const saisi = +(achatTTC / ratio).toFixed(3);
    const vente = parseFloat(String(venteStr).replace(',', '.'));
    const payload = { cost: saisi };
    if (Number.isFinite(vente) && vente >= 0) payload.price = vente;
    try {
      await productService.patchProduct(item.id, payload);
      // Refléter localement sans refetch complet.
      setProducts((prev) => prev.map((pp) => (pp.id === item.id
        ? { ...pp, cost: saisi, ...(payload.price != null ? { price: payload.price } : {}) }
        : pp)));
      setLowStock((prev) => prev.map((r) => (r.id === item.id
        ? { ...r, current_cost: saisi, current_cost_ttc: +achatTTC.toFixed(3) }
        : r)));
      Swal.fire({
        toast: true, position: 'top-end', icon: 'success',
        title: 'Fiche produit mise à jour', timer: 1800, showConfirmButton: false,
      });
    } catch (e) {
      console.error('Erreur mise à jour fiche produit:', e);
      Swal.fire('Erreur', 'Échec de la mise à jour de la fiche produit', 'error');
    }
  };

  const confirmAddPop = () => {
    if (!addPop) return;
    const qty = Math.max(1, parseInt(addPop.qty, 10) || 1);
    const parsed = parseFloat(String(addPop.price).replace(',', '.'));
    const custom = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    const free = addPop.showFree ? Math.max(0, parseInt(addPop.freeQty, 10) || 0) : 0;
    // Ligne existante → la quantité saisie s'AJOUTE (« ajouter encore »).
    const existingLine = cart.find((l) => l.id === addPop.item.id);
    addToCart(addPop.item, existingLine ? existingLine.quantity + qty : qty, custom, free);
    const initial = parseFloat(addPop.initialPrice);
    const changed = custom != null && Number.isFinite(initial) && Math.abs(custom - initial) > 0.0005;
    setAddPop(null);
    // Prix modifié → 2e carte : proposer la mise à jour de la fiche produit.
    // (Pas de fiche pour un nouveau produit réservé pas encore créé.)
    if (changed && !addPop.item.is_new) {
      const p = productById[addPop.item.id] || {};
      const vente = p.price != null ? Number(p.price) : null;
      setFichePop({
        item: addPop.item,
        achat: custom.toFixed(3),
        vente: vente != null ? vente.toFixed(3) : '',
        marge: (vente != null && custom > 0)
          ? (((vente - custom) / custom) * 100).toFixed(1)
          : '',
        editing: false, // d'abord la question ; Oui → champs + focus prix de vente
      });
    }
  };

  const updateQuantity = (id, qty) => {
    if (qty < 1) return;
    setCart((prev) => prev.map((l) => (l.id === id ? { ...l, quantity: qty } : l)));
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((l) => l.id !== id));

  // « Manque » : le fournisseur au téléphone n'a pas ce produit — la ligne est
  // exclue du bon en cours mais reste dans la sélection pour être commandée
  // chez un autre fournisseur.
  const toggleManque = (id) => setCart((prev) => prev.map((l) => (
    l.id === id ? { ...l, manque: !l.manque } : l
  )));

  const resetOrder = () => {
    setCart([]);
    setNotes('');
    setShowPanier(false);
    // Sortir du mode « Continuer la commande » (efface le state de navigation).
    if (continueOrder) {
      navigate('/commandes/a-commander', { replace: true, state: null });
    }
  };

  const activeCart = cart.filter((l) => !l.manque);
  const totalAmount = activeCart.reduce((t, l) => t + priceInfo(l).price * l.quantity, 0);

  // Fournisseur pré-suggéré : celui qui couvre le plus de produits de la
  // sélection. Proposé dès le PREMIER produit ajouté (le panneau de droite
  // affiche le fournisseur en permanence, comme le client sur « Nouvelle
  // vente ») et à l'ouverture du popup « Détails » si toujours vide.
  const suggestSupplier = () => {
    if (panierSupplierId) return;
    const coverage = {};
    cart.forEach((l) => (l.suppliers || []).forEach((s) => {
      coverage[s.supplier_id] = (coverage[s.supplier_id] || 0) + 1;
    }));
    const bestSid = Object.keys(coverage).sort((a, b) => coverage[b] - coverage[a])[0];
    if (bestSid) setPanierSupplierId(bestSid);
  };
  const prevCartLen = useRef(0);
  useEffect(() => {
    if (cart.length > 0 && prevCartLen.current === 0) suggestSupplier();
    prevCartLen.current = cart.length;
  }, [cart]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPanier = () => {
    suggestSupplier();
    setShowPanier(true);
  };

  const buildItemsPayload = (sid) => cart.filter((l) => !l.manque).map((l) => ({
    product: l.is_new ? null : l.id,
    product_name: l.is_new ? l.name : undefined,
    check_status: l.check_status || '',
    quantity: l.quantity,
    free_quantity: l.free_qty || 0,
    expected_cost: l.custom_cost
      ?? (histFor(l.suppliers, sid) ? ttcOf(histFor(l.suppliers, sid)) : null)
      ?? ((l.suppliers || []).length ? ttcOf((l.suppliers || [])[0]) : null)
      ?? (l.cost || 0),
  }));

  // Fournisseur pour un brouillon sauvé à la volée : celui du panier, sinon
  // le filtre actif, sinon celui qui couvre le plus de produits du panier.
  const guessSupplierId = () => {
    if (panierSupplierId) return panierSupplierId;
    const coverage = {};
    cart.forEach((l) => (l.suppliers || []).forEach((s) => {
      coverage[s.supplier_id] = (coverage[s.supplier_id] || 0) + 1;
    }));
    return Object.keys(coverage).sort((a, b) => coverage[b] - coverage[a])[0] || '';
  };

  // Sauve la sélection en brouillon (création ou mise à jour du bon suivi).
  const saveDraft = async () => {
    const sid = guessSupplierId();
    if (!sid) return null;
    const itemsPayload = buildItemsPayload(sid);
    if (continueOrder) {
      await commandeService.updateOrder(continueOrder.id, { notes, items: itemsPayload });
      return continueOrder.id;
    }
    const order = await commandeService.createOrder({
      supplier: parseInt(sid),
      notes,
      items: itemsPayload,
    });
    return order.id;
  };

  const panierSupplier = supplierById(panierSupplierId);
  const uncoveredCount = panierSupplierId
    ? activeCart.filter((l) => l.suppliers && !histFor(l.suppliers, panierSupplierId)).length
    : 0;

  const handleCreate = async () => {
    if (!panierSupplierId) {
      Swal.fire('Fournisseur requis', 'Choisissez le fournisseur de ce bon de commande.', 'info');
      return;
    }
    if (activeCart.length === 0) {
      Swal.fire('Sélection vide', 'Tous les produits sont marqués « manque » — rien à mettre dans ce bon.', 'info');
      return;
    }
    setCreating(true);
    const itemsPayload = buildItemsPayload(panierSupplierId);
    const manqueLines = cart.filter((l) => l.manque);
    // Après création : les lignes « manque » restent dans la sélection pour
    // être commandées chez un autre fournisseur.
    const keepManque = (title) => {
      setCart(manqueLines.map((l) => ({ ...l, manque: false })));
      setShowPanier(false);
      setPanierSupplierId('');
      setNotes('');
      setCreating(false);
      if (continueOrder) navigate('/commandes/a-commander', { replace: true, state: null });
      Swal.fire({
        icon: 'success',
        title,
        text: `${manqueLines.length} produit(s) « manque » gardé(s) dans la sélection — commandez-les chez un autre fournisseur.`,
        confirmButtonColor: KI.ramps.blue[600],
      });
    };
    try {
      if (continueOrder) {
        // Suite d'un brouillon : on remplace les lignes du bon existant.
        await commandeService.updateOrder(continueOrder.id, { notes, items: itemsPayload });
        if (manqueLines.length) { keepManque(`BC #${continueOrder.number} mis à jour`); return; }
        resetOrder();
        navigate(`/commandes/${continueOrder.id}`);
        return;
      }
      const order = await commandeService.createOrder({
        supplier: parseInt(panierSupplierId),
        notes,
        items: itemsPayload,
      });
      if (manqueLines.length) { keepManque(`BC #${order.number} créé`); return; }
      resetOrder();
      navigate(`/commandes/${order.id}`);
    } catch (err) {
      console.error('Erreur enregistrement bon de commande:', err);
      Swal.fire('Erreur', continueOrder
        ? 'Échec de la mise à jour du bon de commande'
        : 'Échec de la création du bon de commande', 'error');
      setCreating(false);
    }
  };

  const catalogShown = catalog.slice(0, visibleCount);

  // Scroll infini : approche du bas du conteneur → tranche suivante.
  const handleCatalogScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 400) {
      setVisibleCount((c) => (c >= catalog.length ? c : c + PAGE_SIZE));
    }
  };

  const stockOptions = [
    { v: 'faible', label: 'À commander', Icon: IcFlame, count: lowStock.length },
    { v: 'rupture', label: 'Rupture', Icon: IcAlertTriangle, count: lowStock.filter((p) => p.stock <= 0).length },
    { v: 'tous', label: 'Tous', Icon: IcPackage, count: products.length },
  ];

  return (
    <div>
      <div className="w-full px-4 pt-2 pb-4">

        {/* Onglets commandes — mêmes chips que VenteTabs; à droite (desktop),
            la bascule Produits/Labo au design du toggle Produits/Services de
            « Nouvelle vente ». Le filtre stock vit désormais dans le bouton
            « Filtres » à côté de la recherche (owner Studio 2026-08-13). */}
        <CommandeTabs
          mobileExtra={(
            /* Mobile : Produits/Labo sur la ligne des onglets, à droite —
               même place que Produits/Services sur « Nouvelle vente »
               (owner Studio 2026-08-14). */
            <div className="inline-flex bg-gray-100 rounded-xl p-1" role="group" aria-label="Mode de recherche">
              {[
                { v: 'produit', label: 'Produits', Icon: IcPackage },
                { v: 'labo', label: 'Labo', Icon: IcFlask },
              ].map(({ v, label, Icon }) => (
                <button
                  type="button"
                  key={v}
                  onClick={() => setSearchMode(v)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${searchMode === v ? 'bg-white text-gray-900 border border-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <Icon size={11} /> {label}
                </button>
              ))}
            </div>
          )}
        >
          <div className="inline-flex bg-gray-100 rounded-xl p-1" role="group" aria-label="Mode de recherche">
            {[
              { v: 'produit', label: 'Produits', Icon: IcPackage },
              { v: 'labo', label: 'Labo', Icon: IcFlask },
            ].map(({ v, label, Icon }) => (
              <button
                type="button"
                key={v}
                onClick={() => setSearchMode(v)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${searchMode === v ? 'bg-white text-gray-900 border border-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
        </CommandeTabs>

        {/* Header desktop réduit au chip « Suite du BC » (porte de l'état) —
            titre, filtre stock et bouton reset « Nouvelle commande » retirés
            (owner Studio 2026-08-13). */}
        {continueOrder && (
          <div className="hidden md:flex items-center gap-3 mb-4 flex-shrink-0 flex-wrap">
            <span className="bdg bdg-warn">
              <IcClipboardList size={10} /> Suite du BC #{continueOrder.number}
            </span>
          </div>
        )}

        {/* Toolbar recherche + Filtres — même design que « Nouvelle vente ».
            Au clic, les résultats s'affichent aussi en liste sous le champ,
            comme la recherche produit d'« Ajouter facture ». */}
        <div className="flex gap-3 mb-3 flex-shrink-0">
          <div className="relative flex-1">
            {/* Composants-2 §2 : 44px, icône search 16px #6B7280, liste
                d'autocomplete collée sous le champ. */}
            <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 z-10" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
              onClick={() => setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              placeholder={searchMode === 'labo'
                ? 'Rechercher par laboratoire / marque…'
                : 'Rechercher un produit (nom ou code-barres)…'}
              className="inp w-full pl-11 pr-9 py-2.5 rounded-xl text-base"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Effacer la recherche"
              >
                <IcClose />
              </button>
            )}
            {searchOpen && (
              <div className="ac-list mt-1 rounded-xl">
                <div className="max-h-72 overflow-y-auto">
                  {searchMatches.length > 0 ? searchMatches.map((item) => {
                    const info = priceInfo(item);
                    return (
                      <button
                        type="button"
                        key={`sr-${item.id}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setSearchOpen(false); openAddPop(item); }}
                        className="ac-row w-full px-4 gap-3 text-left"
                      >
                        <span className="h-9 w-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                          {item.image
                            ? <img src={item.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            : <IcPackage className="text-gray-300" size={14} />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="ac-name block text-sm truncate">{item.name}</span>
                          <span className="ac-sub block text-xs truncate">
                            {item.is_new ? 'Nouveau produit (réservation)' : (item.code ? `Code: ${item.code}` : 'Sans code')}
                          </span>
                        </span>
                        <span className="shrink-0 text-right">
                          <span className={`block text-[10px] font-bold tabular-nums ${item.stock <= 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {item.stock} en stock
                          </span>
                          <span className="ac-sub block text-xs font-semibold">
                            {Number(info.price || 0).toFixed(3)} DT
                          </span>
                        </span>
                      </button>
                    );
                  }) : (
                    <div className="px-4 py-2.5 text-gray-500 text-sm">
                      {searchQuery.trim() ? 'Aucun produit trouvé' : 'Tapez un nom ou un code-barres pour rechercher…'}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={openNewProdPop}
                  className="w-full px-4 py-2.5 text-sm btn-add-block"
                >
                  <IcPlus size={11} />
                  Ajouter un produit{searchQuery.trim() ? ` « ${searchQuery.trim()} »` : ''} — hors catalogue
                </button>
              </div>
            )}
          </div>
          {/* Bascule Produits/Labo mobile déplacée sur la ligne des onglets
              (mobileExtra de CommandeTabs) — owner Studio 2026-08-14. */}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
          >
            <IcFilters className="text-sm" /> Filtres
            {stockFilter !== 'faible' && <span className="bdg-count">1</span>}
          </button>
        </div>

        {/* Panneau Filtres : le filtre stock (À commander / Rupture / Tous) en
            boutons-pilules, même design que le panneau « Filtres » de
            Réservations › Produits (owner 2026-09-09) : pilule active gray-900 /
            blanc, inactive gray-100 / gray-700 ; le compte reste en petit
            chiffre tabulaire dans la pilule. */}
        {showFilters && (
          <div className="flt-card rounded-2xl mb-4 p-4 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Stock">
              {stockOptions.map(({ v, label, count }) => {
                const active = stockFilter === v;
                return (
                  <button
                    type="button"
                    key={v}
                    aria-pressed={active}
                    onClick={() => setStockFilter(v)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors ${active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {label}
                    <span className={`text-xs tabular-nums ${active ? 'text-white/70' : 'text-gray-500'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== Espace de travail + panneau bon de commande =====
            Même architecture que « Nouvelle vente » (SellerSalesPage) : la
            carte catalogue à gauche, un panneau collant à droite, tous deux
            SOUS la barre d'outils. Le rail « Sélection épinglée » a disparu :
            le panneau liste les lignes avec leurs quantités (owner 2026-09-08).
            Côté achat : recherche FOURNISSEUR à la place du client, et pas de
            Remise / Montant payé (les prix réels se saisissent à la facture). */}
        <div className="kp-split">

          {/* ---------- Carte catalogue ---------- */}
          <div className="kp-pane bg-white border border-gray-200 rounded-2xl p-4">
          {/* ===== Catalogue en cartes (défile seul, barre masquée) ===== */}
          <div className="kp-scroll max-h-[60vh] lg:max-h-none pr-1 -mr-1 lg:pr-0 lg:mr-0" onScroll={handleCatalogScroll}>
          {loading ? (
            <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center text-gray-500">Chargement…</div>
          ) : catalogShown.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center">
              {searchQuery || stockFilter !== 'faible' ? (
                <>
                  <IcPackage className="mx-auto text-4xl text-gray-300 mb-3" />
                  <p className="text-gray-500">Aucun produit trouvé</p>
                </>
              ) : (
                <>
                  <IcCheckCircle className="mx-auto text-4xl text-green-400 mb-3" />
                  <p className="text-gray-600 font-medium">Rien à commander</p>
                  <p className="text-sm text-gray-400 mt-1">Tous les stocks sont au-dessus du minimum.</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 pt-2">
                {catalogShown.map((item) => {
                  const inCart = cart.find((l) => l.id === item.id);
                  const info = priceInfo(item);
                  const isOut = item.stock <= 0;
                  return (
                    <div
                      key={item.id}
                      className="kp-lift group relative flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-xl hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-200"
                    >
                      <div
                        className="relative h-32 bg-iface-150 overflow-hidden cursor-pointer"
                        onClick={() => openPricePop(item)}
                        title="Voir les prix d'achat par fournisseur"
                      >
                        {item.image ? (
                          <img src={item.image} alt={item.name} loading="lazy" className="h-full w-full object-contain p-2 bg-white group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-gray-300"><IcPackage size={38} /></div>
                        )}
                        {/* Livre §05 « Carte produit — à commander » : aplat rouge
                            « 0 en stock / min 3 » (10.5px 700) en haut à droite, sans
                            icône ; puis rangée de chips « N vendus (30j) » (or, sans emoji —
                            owner 2026-09-10 —
                            texte seul) et « Réservé ×N » (bleu pointillé). */}
                        {item.is_new ? (
                          <div className="bdg bdg-new bdg-xs absolute top-2 right-2">
                            Nouveau produit
                          </div>
                        ) : isOut ? (
                          <div className="bdg bdg-critical bdg-xs px-2 gap-1 absolute top-2 right-2">
                            0 en stock{item.stock_min ? ` / min ${item.stock_min}` : ''}
                          </div>
                        ) : (
                          <div className="bdg bdg-stock-low bdg-xs absolute top-2 right-2">
                            {item.stock} · Stock bas{item.stock_min ? ` / min ${item.stock_min}` : ''}
                          </div>
                        )}
                        {inCart && (
                          <div className="bdg-count-ink top-2 left-2 right-auto shadow-md ring-2 ring-white">{inCart.quantity}</div>
                        )}
                      </div>
                      <div className="flex flex-col flex-grow p-3">
                        {/* Owner 2026-09-10 : carte de hauteur FIXE comme sur « Nouvelle vente » —
                            la rangée de badges est toujours rendue (22px, sans retour à la
                            ligne) et le badge « BC # » y vit aussi, au lieu de s'empiler sous
                            le prix : plus aucune carte plus haute que sa voisine. */}
                        <div className="flex flex-nowrap items-center gap-1.5 h-[22px] mb-1.5 overflow-hidden">
                          {item.sold_qty > 0 && (
                            <span className="bdg bdg-sold bdg-xs whitespace-nowrap">{item.sold_qty} vendus (30j)</span>
                          )}
                          {item.reserved_qty > 0 && (
                            <span className="bdg bdg-reserve bdg-xs bg-transparent whitespace-nowrap">Réservé ×{item.reserved_qty}</span>
                          )}
                          {item.pending_order && (
                            <Link
                              to={`/commandes/${item.pending_order.order_id}`}
                              className="bdg bdg-warn bdg-xs whitespace-nowrap hover:underline"
                            >
                              <IcAlertTriangle size={8} /> BC #{item.pending_order.order_number}
                            </Link>
                          )}
                        </div>
                        <h4
                          className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2 min-h-[2.5rem] mb-2 cursor-pointer"
                          onClick={() => openPricePop(item)}
                          title="Voir les prix d'achat par fournisseur"
                        >{item.name}</h4>
                        <div className="mt-auto mb-2 leading-none cursor-pointer" onClick={() => openPricePop(item)}>
                          <p className="text-[17px] font-bold text-gray-900 leading-none tabular-nums">
                            {info.price.toFixed(3)}<span className="text-[11px] font-medium text-gray-500 ml-1">DT TTC</span>
                          </p>
                          <p className="text-[11.5px] text-gray-500 mt-1 truncate">
                            {info.exact
                              ? 'dernier prix TTC chez ce fournisseur'
                              : info.from
                                ? <>Meilleur Prix <span className="text-green-600 font-bold">{info.from}</span></>
                                : 'prix d’achat TTC (fiche)'}
                          </p>
                        </div>
                        <button
                          onClick={() => openAddPop(item)}
                          className="btn-p w-full py-2 px-3 gap-1.5 rounded-xl text-[13px] transition-all bg-green-900 hover:bg-green-900 h-[38px]"
                        >
                          <IcPlus size={10} />
                          {inCart ? 'Ajouter encore' : 'Ajouter'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {visibleCount < catalog.length && (
                <p className="text-center text-xs text-gray-400 mt-3">
                  {catalogShown.length} / {catalog.length} produits — défilez pour en afficher plus…
                </p>
              )}
            </>
          )}
          </div>
          </div>

          {/* ---------- Panneau bon de commande ----------
              Hauteur fixe comme la carte catalogue : le panneau ne défile PAS,
              seules les lignes défilent (barre masquée). En-tête = la recherche
              fournisseur ; quand un fournisseur est choisi, sa fiche (nom,
              téléphones, nb de produits à commander) prend la place du champ. */}
          <div className="kp-card kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">

            {/* En-tête : recherche fournisseur */}
            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
              {panierSupplier ? (
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <IcTruck className="text-gray-500 shrink-0" size={13} />
                      <span className="text-base font-bold text-gray-900 truncate" title={panierSupplier.name}>{panierSupplier.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                      {supplierCounts[panierSupplier.id] ? (
                        <span className="bdg bdg-warn text-xs">{supplierCounts[panierSupplier.id]} à commander</span>
                      ) : null}
                      {panierSupplier.phone && panierSupplier.phone.split(',').map((phone) => (
                        <a
                          key={phone}
                          href={`tel:${phone.trim()}`}
                          className="inline-flex items-center gap-1 bg-white hover:bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md text-xs font-bold tabular-nums border border-gray-200"
                        >
                          <IcPhone size={10} /> {phone.trim()}
                        </a>
                      ))}
                    </div>
                    {uncoveredCount > 0 && (
                      <p className="mt-1.5 text-[11px] text-amber-600 flex items-center gap-1">
                        <IcAlertTriangle size={10} /> {uncoveredCount} produit(s) jamais acheté(s) chez ce fournisseur.
                      </p>
                    )}
                  </div>
                  {!continueOrder && (
                    <button
                      type="button"
                      onClick={() => { setPanierSupplierId(''); setPanierSupQuery(''); }}
                      className="h-7 w-7 rounded-full border-[1.5px] border-gray-300 text-gray-500 hover:text-gray-900 hover:bg-gray-50 flex items-center justify-center shrink-0 transition-colors"
                      title="Changer de fournisseur"
                      aria-label="Changer de fournisseur"
                    >
                      <IcClose size={13} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <IcSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={13} />
                  <input
                    type="text"
                    value={panierSupQuery}
                    onChange={(e) => { setPanierSupQuery(e.target.value); setPanierSupOpen(true); }}
                    onFocus={() => setPanierSupOpen(true)}
                    onBlur={() => setTimeout(() => setPanierSupOpen(false), 150)}
                    placeholder="Rechercher un fournisseur..."
                    aria-label="Rechercher un fournisseur"
                    className="inp w-full pl-9 pr-3 py-2.5 text-sm"
                  />
                  {panierSupOpen && panierSupMatches.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white rounded-lg shadow-lg border border-gray-100 py-1 max-h-52 overflow-y-auto">
                      {panierSupMatches.map((sup) => (
                        <button
                          type="button"
                          key={sup.id}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setPanierSupplierId(String(sup.id)); setPanierSupQuery(''); setPanierSupOpen(false); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center justify-between gap-2"
                        >
                          <span className="text-sm font-medium text-gray-800 truncate">{sup.name}</span>
                          <span className="flex items-center gap-1.5 shrink-0">
                            {sup.phone && <span className="text-xs text-gray-400">{sup.phone.split(',')[0].trim()}</span>}
                            {supplierCounts[sup.id] ? (
                              <span className="bdg bdg-warn text-[10px] py-[2px]">{supplierCounts[sup.id]}</span>
                            ) : null}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Lignes — seule zone qui défile (barre masquée). Ligne = image +
                nom + prix total + pas de quantité, ✕ en haut à droite comme
                sur « Nouvelle vente ». Une ligne « manque » reste visible en
                ambre, hors bon ; elle se remet dans le bon via « Détails ». */}
            <div className="kp-scroll p-3 space-y-2">
              {cart.length === 0 ? (
                <div className="h-full flex items-center justify-center p-4">
                  <div className="empty-ki w-full px-4 py-8">
                    <IcClipboardList size={28} className="empty-ki-icon w-[28px] h-[28px] mb-2" />
                    <p className="empty-ki-title text-sm mt-0">Aucun produit</p>
                    <p className="empty-ki-sub mb-0">Cherchez un produit ou cliquez sur une carte du catalogue.</p>
                  </div>
                </div>
              ) : cart.map((l) => {
                const info = priceInfo(l);
                return (
                  <div key={`ln-${l.id}`} className={`kp-pin-chip group/line relative rounded-xl border bg-white px-3 py-2.5 transition-colors ${l.manque ? 'border-amber-200 bg-amber-50/60' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                    <div className="flex items-start gap-2.5">
                      <span className="h-10 w-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {l.image
                          ? <img src={l.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          : <IcPackage size={16} className="text-gray-300" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className={`text-[13px] font-semibold leading-snug truncate pr-6 ${l.manque ? 'text-amber-800 line-through' : 'text-gray-900'}`} title={l.name}>{l.name}</p>
                        <div className="flex items-center justify-between gap-2 mt-1.5">
                          {l.manque ? (
                            <span className="text-[11px] font-semibold text-amber-600">manque — hors bon</span>
                          ) : (
                            <span className="text-[13px] font-bold text-gray-900 tabular-nums whitespace-nowrap">
                              {(info.price * l.quantity).toFixed(3)}<span className="text-[10px] font-medium text-gray-400 ml-1">DT</span>
                              {l.free_qty > 0 && (
                                <span className="bdg bdg-free ml-1.5">+{l.free_qty} gratuit{l.free_qty > 1 ? 's' : ''}</span>
                              )}
                            </span>
                          )}
                          {!l.manque && (
                            <span className="flex items-center gap-1 flex-shrink-0">
                              <button type="button" onClick={() => updateQuantity(l.id, l.quantity - 1)} aria-label="Diminuer" className="h-6 w-6 rounded-md bg-white border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600 flex items-center justify-center transition-all"><IcMinus size={8} /></button>
                              <span className="w-5 text-center text-xs font-extrabold text-gray-800 tabular-nums">{l.quantity}</span>
                              <button type="button" onClick={() => updateQuantity(l.id, l.quantity + 1)} aria-label="Augmenter" className="h-6 w-6 rounded-md bg-white border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600 flex items-center justify-center transition-all"><IcPlus size={8} /></button>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeFromCart(l.id)} title="Retirer de la commande"
                      className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full border-[1.5px] border-gray-300 text-gray-500 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors">
                      <IcClose size={13} />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Pied — total estimé (point focal), puis les deux actions sur
                la MÊME ligne : « Détails » sur 1/3, action principale sur 2/3
                (Créer = primaire vert, Mettre à jour = encre). Pas de Remise
                ni de Montant payé côté achat (owner 2026-09-08). */}
            <div className="p-3 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-sm font-medium text-gray-500">Total estimé</span>
                <span className="text-2xl font-bold text-gray-900 tabular-nums">{totalAmount.toFixed(3)} DT</span>
              </div>
              <p className="text-[11px] text-gray-400 mb-2.5">
                Prix estimés d'après les derniers achats — les prix réels seront saisis sur la facture à la réception.
              </p>
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={openPanier} className="btn-g col-span-1 py-2.5 rounded-xl text-xs transition-colors">
                  Détails
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating || cart.length === 0 || !panierSupplierId}
                  className={`col-span-2 py-2.5 rounded-xl text-sm gap-2 transition-all ${continueOrder ? 'btn-ink' : 'btn-p'}`}
                >
                  {creating
                    ? (<><BtnSk /> Enregistrement…</>)
                    : continueOrder
                      ? (<><IcClipboardList size={14} /> Mettre à jour le bon (BC #{continueOrder.number})</>)
                      : (<><IcClipboardList size={14} /> Créer le bon de commande</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Popup « Nouveau produit » : produit hors catalogue ajouté au
          bon par sa désignation seule (comme « Passer une commande ») ===== */}
      {newProdPop && createPortal(
        (() => {
          const qtyNum = Math.max(1, parseInt(newProdPop.qty, 10) || 1);
          const costNum = parseFloat(String(newProdPop.cost).replace(',', '.'));
          const lineTotal = (Number.isFinite(costNum) ? costNum : 0) * qtyNum;
          return (
            <div className="modal-backdrop z-[80] flex items-center justify-center p-4" onClick={() => setNewProdPop(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Composants-2 §10 : entête aplat encre, ✕ gris → blanc */}
                <div className="modal-head items-start gap-3 py-3.5">
                  <div className="flex-1 min-w-0">
                    <h3 className="modal-title leading-tight flex items-center gap-2"><IcPlus size={15} /> Nouveau produit</h3>
                    <p className="text-xs text-gray-300 mt-0.5 leading-tight">Hors catalogue — seul son nom part sur le bon de commande</p>
                  </div>
                  <button onClick={() => setNewProdPop(null)} className="h-8 w-8 rounded-full text-gray-400 hover:text-white flex items-center justify-center shrink-0" aria-label="Fermer"><IcClose size={13} /></button>
                </div>

                <div className="p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Nom du produit <span className="text-red-600">*</span></label>
                    <input
                      ref={newProdNameRef}
                      type="text"
                      value={newProdPop.name}
                      onChange={(e) => setNewProdPop((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Ex : Crème hydratante XYZ"
                      className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${newProdMatches.exact ? 'border-amber-300' : 'border-gray-200'}`}
                    />
                  </div>

                  {/* Doublon exact : ce nom est déjà une fiche du catalogue */}
                  {newProdMatches.exact && (
                    <div role="alert" className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                      <p className="flex items-center gap-1.5 text-xs font-bold text-amber-700">
                        <IcAlertTriangle size={11} /> Ce produit existe déjà au catalogue
                      </p>
                      <div className="mt-2 flex items-center gap-2.5 rounded-lg bg-white border border-amber-100 px-2.5 py-2">
                        <span className="h-9 w-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                          {newProdMatches.exact.image
                            ? <img src={newProdMatches.exact.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            : <IcPackage className="text-gray-300" size={14} />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-gray-800 truncate">{newProdMatches.exact.name}</span>
                          <span className="block text-[11px] text-gray-400 truncate">
                            {newProdMatches.exact.code ? `Code: ${newProdMatches.exact.code}` : 'Sans code'}
                          </span>
                        </span>
                        <span className={`shrink-0 text-[10px] font-bold ${(newProdMatches.exact.stock ?? 0) <= 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {newProdMatches.exact.stock ?? 0} en stock
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => pickExistingProduct(newProdMatches.exact)}
                          className="py-2 text-xs btn-p"
                        >
                          Utiliser ce produit
                        </button>
                        <button
                          type="button"
                          onClick={() => { newProdNameRef.current?.focus(); newProdNameRef.current?.select(); }}
                          className="py-2 text-xs btn-g-edit"
                        >
                          Modifier le nom
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Noms proches : peut-être une fiche existante mal orthographiée */}
                  {!newProdMatches.exact && newProdMatches.similar.length > 0 && (
                    <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <p className="text-xs font-bold text-accent">Produits proches au catalogue — est-ce l'un d'eux ?</p>
                      <div className="mt-1.5 space-y-1">
                        {newProdMatches.similar.map((p) => (
                          <button
                            type="button"
                            key={`dup-${p.id}`}
                            onClick={() => pickExistingProduct(p)}
                            className="w-full flex items-center gap-2.5 rounded-app bg-white border border-gray-200 hover:border-accent hover:bg-blue-50 px-2.5 py-1.5 text-left"
                            title="Utiliser ce produit du catalogue"
                          >
                            <span className="h-8 w-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                              {p.image
                                ? <img src={p.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                : <IcPackage className="text-gray-300" size={13} />}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-[13px] font-medium text-gray-800 truncate">{p.name}</span>
                              <span className="block text-[10px] text-gray-400 truncate">{p.code ? `Code: ${p.code}` : 'Sans code'} · {p.stock ?? 0} en stock</span>
                            </span>
                            <span className="shrink-0 text-[11px] font-bold text-accent">Utiliser</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix d'achat estimé TTC</label>
                      <div className="relative">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.001"
                          value={newProdPop.cost}
                          onChange={(e) => setNewProdPop((p) => ({ ...p, cost: e.target.value }))}
                          onFocus={(e) => e.target.select()}
                          placeholder="0.000"
                          className="w-full pl-3 pr-12 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 tabular-nums font-semibold"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">DT</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Quantité</label>
                      <div className="flex items-stretch border border-gray-200 rounded-xl overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setNewProdPop((p) => ({ ...p, qty: String(Math.max(1, (parseInt(p.qty, 10) || 1) - 1)) }))}
                          className="px-3 bg-white hover:bg-gray-100 text-gray-900 flex items-center justify-center"
                        ><IcMinus size={10} /></button>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          value={newProdPop.qty}
                          onChange={(e) => setNewProdPop((p) => ({ ...p, qty: e.target.value }))}
                          onFocus={(e) => e.target.select()}
                          className="w-full text-center py-2.5 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-semibold"
                        />
                        <button
                          type="button"
                          onClick={() => setNewProdPop((p) => ({ ...p, qty: String((parseInt(p.qty, 10) || 0) + 1) }))}
                          className="px-3 bg-white hover:bg-gray-100 text-gray-900 flex items-center justify-center"
                        ><IcPlus size={10} /></button>
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-400 mt-2">
                    Prix estimé facultatif — le prix réel sera saisi sur la facture à la réception.
                  </p>

                  <div className="flex items-center justify-between mt-4 px-1">
                    <span className="text-sm text-gray-500">Total ligne estimé</span>
                    <span className="text-xl font-extrabold text-blue-600 tabular-nums">
                      {lineTotal.toFixed(3)} <span className="text-sm text-gray-400 font-semibold">DT</span>
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={confirmNewProdPop}
                    className="w-full mt-4 py-3 text-sm btn-add-pop transition-all"
                  >
                    <IcPlus size={11} /> Ajouter à la commande
                  </button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* ===== 2e carte : prix d'achat modifié → proposer la mise à jour de
          la fiche produit (achat, vente, marge modifiables et liés) ===== */}
      {fichePop && createPortal(
        (() => {
          const margeNum = parseFloat(fichePop.marge);
          return (
            <div className="modal-backdrop z-[85] flex items-center justify-center p-4" onClick={() => setFichePop(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="modal-head items-start gap-3 py-3.5">
                  <div className="flex-1 min-w-0">
                    <h3 className="modal-title leading-tight flex items-center gap-2"><IcPackage size={15} /> Mettre à jour la fiche produit ?</h3>
                    <p className="text-xs text-gray-300 mt-0.5 leading-tight line-clamp-2">{fichePop.item.name}</p>
                  </div>
                  <button onClick={() => setFichePop(null)} className="h-8 w-8 rounded-full text-gray-400 hover:text-white flex items-center justify-center shrink-0" aria-label="Fermer"><IcClose size={13} /></button>
                </div>

                <div className="p-4">
                  {/* La question d'abord, en haut. */}
                  <p className="text-sm text-gray-600">
                    Nouveau prix d'achat TTC : <span className="font-bold text-gray-800 tabular-nums">{Number(parseFloat(fichePop.achat) || 0).toFixed(3)} DT</span>
                    <span className="text-gray-400"> — modifier aussi la fiche (prix de vente, marge) ?</span>
                  </p>

                  {!fichePop.editing ? (
                    <div className="flex justify-end gap-3 mt-4">
                      <button
                        type="button"
                        onClick={() => setFichePop(null)}
                        className="px-4 py-3 rounded-xl text-sm btn-g transition-all"
                      >
                        Non
                      </button>
                      <button
                        type="button"
                        onClick={() => setFichePop((f) => ({ ...f, editing: true }))}
                        className="px-5 py-3 rounded-xl text-sm btn-ink transition-all"
                      >
                        Oui, modifier
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix d'achat TTC</label>
                          <div className="relative">
                            <input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="0.001"
                              value={fichePop.achat}
                              onChange={(e) => setFicheAchat(e.target.value)}
                              onFocus={(e) => e.target.select()}
                              className="w-full pl-3 pr-12 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 tabular-nums font-semibold"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">DT</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix de vente</label>
                          <div className="relative">
                            <input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="0.001"
                              value={fichePop.vente}
                              onChange={(e) => setFicheVente(e.target.value)}
                              onFocus={(e) => e.target.select()}
                              className="w-full pl-3 pr-12 py-2.5 border border-green-300 ring-1 ring-green-100 rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-semibold"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">DT</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Marge</label>
                        <div className="relative max-w-[11rem]">
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.1"
                            value={fichePop.marge}
                            onChange={(e) => setFicheMarge(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            className={`w-full pl-3 pr-9 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-bold ${Number.isFinite(margeNum) && margeNum < 0 ? 'text-red-600' : 'text-green-700'}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">%</span>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1.5">
                          Achat, vente et marge sont liés — modifiez ce qu'il faut.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={saveFiche}
                        disabled={fichePop.saving}
                        className="w-full mt-4 py-3 text-sm btn-p transition-all"
                      >
                        {fichePop.saving ? 'Enregistrement…' : 'Enregistrer la fiche'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* ===== Popup « Prix fournisseurs » : historique d'achat TTC par
          fournisseur, meilleur prix en premier — clic sur une ligne =
          préremplit l'ajout à ce prix ===== */}
      {pricePop && createPortal(
        (() => {
          const { item, loading: popLoading } = pricePop;
          const suppliersList = item.suppliers || [];
          return (
            <div className="modal-backdrop z-[80] flex items-center justify-center p-4" onClick={() => setPricePop(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="modal-head items-start gap-3 py-3.5">
                  <span className="h-14 w-14 rounded-xl bg-white flex items-center justify-center overflow-hidden shrink-0">
                    {item.image
                      ? <img src={item.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      : <IcPackage className="text-gray-300" size={22} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="modal-title text-sm leading-tight line-clamp-2">{item.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {item.stock <= 0 ? (
                        <span className="bdg bdg-rupture">Rupture</span>
                      ) : (
                        <span className="bdg bdg-stock-low">{item.stock} · Stock bas</span>
                      )}
                      {item.reserved_qty > 0 && (
                        <span className="bdg bdg-reserve">
                          Réservé ×{item.reserved_qty}
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => setPricePop(null)} className="h-8 w-8 rounded-full text-gray-400 hover:text-white flex items-center justify-center shrink-0" aria-label="Fermer"><IcClose size={13} /></button>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">
                    Prix d'achat TTC par fournisseur
                  </p>
                  {popLoading ? (
                    <div className="py-8 text-center text-sm text-gray-400">Chargement de l'historique…</div>
                  ) : suppliersList.length === 0 ? (
                    <div className="py-6 text-center">
                      <IcTruck className="mx-auto text-2xl text-gray-200 mb-2" />
                      <p className="text-sm text-gray-500">Jamais acheté sur facture.</p>
                      <p className="text-xs text-gray-400 mt-1 tabular-nums">
                        Prix d'achat TTC de la fiche : <span className="font-semibold text-gray-600">{Number(item.current_cost_ttc ?? item.cost ?? 0).toFixed(3)} DT</span>
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {suppliersList.map((s, idx) => {
                        const isBest = idx === 0;
                        return (
                          <button
                            type="button"
                            key={s.supplier_id}
                            onClick={() => chooseSupplierPrice(s)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                              isBest
                                ? 'bg-green-50 border-green-200 hover:border-green-300'
                                : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'
                            }`}
                          >
                            <span className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-extrabold ${
                              isBest ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {isBest ? <IcTrophy size={11} /> : idx + 1}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="flex items-center gap-1.5">
                                <span className="text-sm font-semibold text-gray-800 truncate">{s.supplier_name}</span>
                                {isBest && (
                                  <span className="bdg bdg-best shrink-0">Meilleur prix</span>
                                )}
                              </span>
                              <span className="block text-[11px] text-gray-400 tabular-nums">
                                {s.last_date ? new Date(s.last_date).toLocaleDateString('fr-FR') : ''} · {s.purchase_count} achat{s.purchase_count > 1 ? 's' : ''}
                              </span>
                            </span>
                            <span className="shrink-0 text-right">
                              <span className={`block text-base font-extrabold tabular-nums leading-none ${isBest ? 'text-green-700' : 'text-gray-800'}`}>
                                {Number(ttcOf(s)).toFixed(3)}
                              </span>
                              <span className="block text-[10px] font-semibold text-gray-400 mt-0.5">DT TTC</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                  <p className="text-[11px] text-gray-400 mb-2.5">
                    Prix TTC = dernier prix facturé chez chaque fournisseur, TVA comprise. Touchez un fournisseur pour commander à ce prix.
                  </p>
                  <button
                    type="button"
                    onClick={() => { const it = pricePop.item; setPricePop(null); openAddPop(it); }}
                    className="w-full py-2.5 text-sm btn-add-pop transition-all"
                  >
                    <IcPlus size={11} /> Ajouter à la commande
                  </button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* ===== Popup « Ajouter » : prix + quantité (même design que l'aperçu
          produit de la vente : portal, max-w-sm, image en tête) ===== */}
      {addPop && createPortal(
        (() => {
          const { item } = addPop;
          const inCart = cart.find((l) => l.id === item.id);
          // Prix de référence (dernier/meilleur prix), en ignorant un prix saisi.
          const base = priceInfo({ ...(inCart || item), custom_cost: null });
          const qtyNum = Math.max(1, parseInt(addPop.qty, 10) || 1);
          const priceNum = parseFloat(String(addPop.price).replace(',', '.'));
          const lineTotal = (Number.isFinite(priceNum) ? priceNum : 0) * qtyNum;
          return (
            <div className="modal-backdrop z-[80] flex items-center justify-center p-4" onClick={() => setAddPop(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="relative h-64 bg-white flex items-center justify-center p-4 border-b border-gray-100">
                  {item.image
                    ? <img src={item.image} alt="" className="max-h-full max-w-full object-contain" />
                    : <IcPackage size={72} className="text-gray-200" />}
                  <button onClick={() => setAddPop(null)} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center"><IcClose size={13} /></button>
                  {item.stock <= 0 ? (
                    <div className="bdg bdg-rupture absolute top-2 left-2">Rupture</div>
                  ) : (
                    <div className="bdg bdg-stock-low absolute top-2 left-2">
                      {item.stock} · Stock bas{item.stock_min ? ` / min ${item.stock_min}` : ''}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-lg leading-tight">{item.name}</h3>
                  <p className="text-[11px] text-gray-400 mt-1">
                    {base.exact
                      ? 'dernier prix TTC chez ce fournisseur'
                      : base.from
                        ? <>meilleur prix TTC connu — <span className="text-green-600 font-semibold">{base.from}</span></>
                        : 'prix d’achat TTC (fiche)'}
                    {' : '}<span className="font-semibold text-gray-600 tabular-nums">{Number(base.price || 0).toFixed(3)} DT TTC</span>
                  </p>

                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix d'achat TTC</label>
                      <div className="relative">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.001"
                          value={addPop.price}
                          onChange={(e) => setAddPop((p) => ({ ...p, price: e.target.value }))}
                          onFocus={(e) => e.target.select()}
                          className="w-full pl-3 pr-12 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 tabular-nums font-semibold"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">DT</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Quantité</label>
                      <div className="flex items-stretch border border-gray-200 rounded-xl overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setAddPop((p) => ({ ...p, qty: String(Math.max(1, (parseInt(p.qty, 10) || 1) - 1)) }))}
                          className="px-3 bg-white hover:bg-gray-100 text-gray-900 flex items-center justify-center"
                        ><IcMinus size={10} /></button>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          value={addPop.qty}
                          onChange={(e) => setAddPop((p) => ({ ...p, qty: e.target.value }))}
                          onFocus={(e) => e.target.select()}
                          className="w-full text-center py-2.5 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-semibold"
                        />
                        <button
                          type="button"
                          onClick={() => setAddPop((p) => ({ ...p, qty: String((parseInt(p.qty, 10) || 0) + 1) }))}
                          className="px-3 bg-white hover:bg-gray-100 text-gray-900 flex items-center justify-center"
                        ><IcPlus size={10} /></button>
                      </div>
                    </div>
                  </div>

                  {/* Unités gratuites offertes par le fournisseur (ex: 10 + 2) */}
                  <div className={`mt-3 rounded-xl border transition-colors ${addPop.showFree ? 'border-green-200 bg-green-50/60' : 'border-gray-200'}`}>
                    <label className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!addPop.showFree}
                        onChange={(e) => setAddPop((p) => ({ ...p, showFree: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-gray-900"
                      />
                      {/* Studio owner 2026-09-10 : libellé court, sans icône cadeau. */}
                      <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        Produit gratuit
                      </span>
                    </label>
                    {addPop.showFree && (
                      <div className="px-3 pb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Quantité gratuite</label>
                        <div className="flex items-stretch border border-green-200 bg-white rounded-xl overflow-hidden max-w-[11rem]">
                          <button
                            type="button"
                            onClick={() => setAddPop((p) => ({ ...p, freeQty: String(Math.max(0, (parseInt(p.freeQty, 10) || 0) - 1)) }))}
                            className="px-3 bg-white hover:bg-gray-100 text-gray-900 flex items-center justify-center"
                          ><IcMinus size={10} /></button>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            value={addPop.freeQty}
                            onChange={(e) => setAddPop((p) => ({ ...p, freeQty: e.target.value }))}
                            onFocus={(e) => e.target.select()}
                            className="w-full text-center py-2 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-semibold"
                          />
                          <button
                            type="button"
                            onClick={() => setAddPop((p) => ({ ...p, freeQty: String((parseInt(p.freeQty, 10) || 0) + 1) }))}
                            className="px-3 bg-white hover:bg-gray-100 text-gray-900 flex items-center justify-center"
                          ><IcPlus size={10} /></button>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1.5">
                          Gratuit : ne compte pas dans le total — deviendra une ligne gratuite sur la facture.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-4 px-1">
                    <span className="text-sm text-gray-500 flex items-center gap-2">
                      Total ligne
                      {addPop.showFree && (parseInt(addPop.freeQty, 10) || 0) > 0 && (
                        <span className="bdg bdg-free">
                          +{parseInt(addPop.freeQty, 10) || 0} gratuit{(parseInt(addPop.freeQty, 10) || 0) > 1 ? 's' : ''}
                        </span>
                      )}
                    </span>
                    <span className="text-xl font-extrabold text-gray-900 tabular-nums">
                      {lineTotal.toFixed(3)} <span className="text-sm text-gray-400 font-semibold">DT</span>
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={confirmAddPop}
                    className="w-full mt-4 py-3 text-sm btn-add-pop transition-all"
                  >
                    <IcPlus size={11} /> {inCart ? 'Ajouter encore' : 'Ajouter à la commande'}
                  </button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* ===== Panier popup : finaliser le bon de commande ===== */}
      {showPanier && (
        // Popup au design du panier « Nouvelle vente » (owner Studio 2026-08-13) —
        // mêmes en-tête sombre, cartes de lignes et pied; champs inchangés.
        <div className="modal-backdrop z-[80] flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowPanier(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {/* Composants-2 §10 panier : entête aplat encre #111827 */}
            <div className="bg-gray-900 text-white px-5 py-3 flex items-center justify-between flex-shrink-0">
              {/* Studio 2026-08-14 (mobile only) : textes/icône gray-100, badge ink — desktop inchangé via md:. */}
              <span className="font-semibold flex items-center gap-2 flex-wrap text-gray-100 md:text-white">
                <IcClipboardList className="text-gray-100 md:text-white" /> Bon de commande
                {panierSupplier && (
                  <span className="text-gray-100 md:text-white/80 font-normal truncate max-w-[12rem]">· {panierSupplier.name}</span>
                )}
                <span className="bdg bdg-ink"><IcPackage size={11} /> {cart.length}</span>
              </span>
              <button onClick={() => setShowPanier(false)} className="text-gray-400 hover:text-white p-1.5 rounded-full hover:bg-white/10" aria-label="Fermer"><IcClose /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Fournisseur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
                {panierSupplier ? (
                  <div className="w-full pl-4 pr-3 py-2 border border-gray-900 bg-gray-50 rounded-xl flex items-center gap-3 flex-wrap min-h-[46px]">
                    <IcTruck className="text-gray-600 shrink-0" />
                    <span className="font-semibold text-gray-900">{panierSupplier.name}</span>
                    {supplierCounts[panierSupplier.id] ? (
                      /* Studio 2026-08-14 : masqué sur mobile, desktop inchangé. */
                      <span className="bdg bdg-warn hidden md:inline-flex">
                        {supplierCounts[panierSupplier.id]} à commander
                      </span>
                    ) : null}
                    {panierSupplier.phone && panierSupplier.phone.split(',').map((phone) => (
                      <a
                        key={phone}
                        href={`tel:${phone.trim()}`}
                        className="inline-flex items-center gap-1.5 bg-white hover:bg-gray-100 text-gray-700 px-2.5 py-1 rounded-lg font-bold text-sm tabular-nums border border-gray-200"
                      >
                        <IcPhone size={11} /> {phone.trim()}
                      </a>
                    ))}
                    {!continueOrder && (
                      <button
                        type="button"
                        onClick={() => { setPanierSupplierId(''); setPanierSupQuery(''); }}
                        className="ml-auto text-gray-400 hover:text-gray-700 p-1"
                        title="Changer de fournisseur"
                      >
                        <IcClose />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <IcTruck className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 z-10" />
                    <input
                      type="text"
                      value={panierSupQuery}
                      onChange={(e) => { setPanierSupQuery(e.target.value); setPanierSupOpen(true); }}
                      onFocus={() => setPanierSupOpen(true)}
                      onBlur={() => setPanierSupOpen(false)}
                      placeholder="Écrire le nom du fournisseur…"
                      className="inp w-full pl-11 pr-4 py-2.5 rounded-xl text-base"
                    />
                    {panierSupOpen && panierSupMatches.length > 0 && (
                      <div className="ac-list mt-1 rounded-xl max-h-60 overflow-y-auto">
                        {panierSupMatches.map((s) => (
                          <button
                            type="button"
                            key={s.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setPanierSupplierId(String(s.id));
                              setPanierSupQuery('');
                              setPanierSupOpen(false);
                            }}
                            className="ac-row w-full px-4 text-base gap-3 text-left"
                          >
                            <span className="ac-name truncate">{s.name}</span>
                            <span className="flex items-center gap-2 shrink-0">
                              {s.phone && (
                                <span className="ac-sub text-xs">{s.phone.split(',')[0].trim()}</span>
                              )}
                              {supplierCounts[s.id] ? (
                                <span className="bdg bdg-warn">
                                  {supplierCounts[s.id]} à commander
                                </span>
                              ) : null}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {uncoveredCount > 0 && (
                  <p className="mt-2 text-xs text-amber-600 hidden md:flex items-center gap-1.5">
                    <IcAlertTriangle size={10} />
                    {uncoveredCount} produit(s) du panier jamais acheté(s) chez ce fournisseur.
                  </p>
                )}
              </div>

              {/* Lignes — cartes comme le panier « Nouvelle vente » */}
              <div className="space-y-2">
                {cart.map((l) => {
                  const hist = panierSupplierId ? histFor(l.suppliers, panierSupplierId) : null;
                  const best = (l.suppliers || [])[0];
                  const price = l.custom_cost ?? (hist ? ttcOf(hist) : (best ? ttcOf(best) : (l.cost || 0)));
                  return (
                    <div key={l.id} className={`rounded-xl p-3 shadow-sm border flex items-center gap-3 ${l.manque ? 'bg-amber-50/60 border-amber-200' : 'bg-white border-gray-200'}`}>
                      {/* Studio 2026-08-14 : vignette et bouton « manque » masqués sur mobile (desktop inchangé). */}
                      <span className="h-9 w-9 rounded-lg bg-gray-50 border border-gray-100 hidden md:flex items-center justify-center overflow-hidden shrink-0">
                        {l.image
                          ? <img src={l.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          : <IcPackage className="text-gray-300" size={14} />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold truncate ${l.manque ? 'text-amber-800 line-through' : 'text-gray-800'}`}>{l.name}</div>
                        <div className="text-xs text-gray-400 tabular-nums">
                          {l.manque ? (
                            <span className="text-amber-600 font-semibold">manque chez ce fournisseur — hors bon</span>
                          ) : (
                            <>
                              {price.toFixed(3)} DT
                              {l.free_qty > 0 && (
                                <span className="text-green-700 font-semibold ml-1.5">+{l.free_qty} gratuit{l.free_qty > 1 ? 's' : ''}</span>
                              )}
                              {panierSupplierId && !hist && l.suppliers && (
                                <span className="text-amber-600 ml-1.5 hidden md:inline">(jamais acheté ici)</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {!l.manque && (
                        <span className="flex items-center gap-2 shrink-0">
                          {/* Boutons-2 §7 stepper : cases 40px blanches texte encre, groupe bordé #D1D5DB rayon 6, valeur 600 tabulaire */}
                          <span className="hidden md:inline-flex items-center border border-gray-300 rounded-lg overflow-hidden">
                            <button type="button" onClick={() => updateQuantity(l.id, l.quantity - 1)} className="w-9 h-9 flex items-center justify-center bg-white hover:bg-gray-100 text-gray-900 transition"><IcMinus className="text-xs" /></button>
                            <span className="w-8 text-center font-semibold tabular-nums">{l.quantity}</span>
                            <button type="button" onClick={() => updateQuantity(l.id, l.quantity + 1)} className="w-9 h-9 flex items-center justify-center bg-white hover:bg-gray-100 text-gray-900 transition"><IcPlus className="text-xs" /></button>
                          </span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="1"
                            value={l.quantity}
                            onChange={(e) => { const v = parseInt(e.target.value, 10); if (v >= 1) updateQuantity(l.id, v); }}
                            onFocus={(e) => e.target.select()}
                            className="md:hidden w-12 h-9 text-center font-bold tabular-nums bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <button type="button" onClick={() => updateQuantity(l.id, l.quantity + 1)} className="w-9 h-9 hidden md:flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition"><IcPlus className="text-xs" /></button>
                        </span>
                      )}
                      {!l.manque && (
                        <span className="w-20 text-right text-sm font-bold text-gray-800 tabular-nums shrink-0">
                          {(price * l.quantity).toFixed(3)}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleManque(l.id)}
                        title={l.manque ? 'Remettre dans le bon' : 'Le fournisseur n\'a pas ce produit'}
                        className={`shrink-0 h-8 w-8 rounded-lg border hidden md:flex items-center justify-center transition-all ${
                          l.manque
                            ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                            : 'border-amber-300 text-amber-600 hover:bg-amber-50'
                        }`}
                      >
                        <IcAlertTriangle size={12} />
                      </button>
                      <button type="button" onClick={() => removeFromCart(l.id)} className="p-1 flex items-center justify-center text-red-600 hover:bg-red-50 shrink-0 transition-colors" title="Retirer">
                        <IcClose />
                      </button>
                    </div>
                  );
                })}
                {cart.length === 0 && (
                  <div className="text-center py-10 text-gray-400">
                    <IcClipboardList size={32} className="mx-auto mb-3 text-gray-300" />
                    <p className="font-semibold text-gray-600">Votre bon de commande est vide</p>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 text-sm"
                  placeholder="Notes additionnelles..."
                ></textarea>
              </div>
            </div>

            <div className="flex-shrink-0 border-t border-gray-100 p-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-gray-600 font-medium">Total estimé</span>
                <span className="text-xl font-extrabold text-gray-900 tabular-nums">{totalAmount.toFixed(3)} <span className="text-sm font-semibold text-gray-400">DT</span></span>
              </div>
              <p className="text-[11px] text-gray-400 mb-3">
                Prix estimés d'après les derniers achats — les prix réels seront saisis sur la facture à la réception.
              </p>
              <div className="flex items-center gap-3">
                {/* §10 pied panier : sortie ghost + action principale (Créer = primaire vert, Mettre à jour = encre) */}
                <button onClick={() => setShowPanier(false)} className="px-5 py-2.5 rounded-xl text-sm btn-g">Continuer</button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating || cart.length === 0 || !panierSupplierId}
                  className={`flex-1 py-2.5 transition-all ${continueOrder ? 'btn-ink' : 'btn-p'}`}
                >
                  {creating
                    ? 'Enregistrement…'
                    : continueOrder
                      ? `Mettre à jour le bon (BC #${continueOrder.number})`
                      : 'Créer le bon de commande'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ACommander;
